import { apiClient } from './apiClient';

export enum Units {
  Piece = 0,
  Liter = 1,
  Pinch = 2,
  Deciliter = 3,
  Clove = 4,
  Kilogram = 5,
  Gram = 6,
  TableSpoon = 7,
  TeaSpoon = 8,
  Milliliter = 9,
  Handful = 10,
  Sprig = 11,
  Crucible = 12,
  Can = 13,
  Slice = 14,
  Volume = 15,
  Stem = 16,
  Cup = 17,
  Mug = 18,
  GlassOf = 19,
  Cube = 20,
  Package = 21,
  Bowl = 22,
  Bottle = 23,
}

export interface FoodDto {
  id: string;
  name: string;
  recipe: string;
  durationMinutes: number;
  categoryNames: string[];
  categories: CategoryDto[];
  ingredients: FoodIngredientDto[];
  images: FoodImageDto[];
  peopleCount: number;
  created: string;
  updated?: string;
  averageRating: number;
  ratingCount: number;
  isVegetarian: boolean;
}

export interface CategoryDto {
  id: string;
  name: string;
}

export interface FoodIngredientDto {
  id: string;
  ingredientId: string;
  ingredientName: string;
  quantity: number;
  units: Units;
}

export interface FoodImageDto {
  id: string;
  path: string;
}

export interface CreateFoodRequest {
  name: string;
  recipe: string;
  durationMinutes: number;
  categoryNames: string[];
  peopleCount: number;
}

export interface UpdateFoodRequest {
  id: string;
  name: string;
  recipe: string;
  durationMinutes: number;
  categoryNames: string[];
  peopleCount: number;
}

export interface AddFoodIngredientRequest {
  foodId: string;
  ingredientId: string;
  quantity: number;
  units: Units;
}

export interface AddFoodImageRequest {
  foodId: string;
  fileName: string;
  base64Image: string;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface GetFoodsParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

export async function getFoods(params: GetFoodsParams = {}): Promise<PagedResult<FoodDto>> {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set('page', params.page.toString());
  if (params.pageSize) queryParams.set('pageSize', params.pageSize.toString());
  if (params.search) queryParams.set('search', params.search);
  
  const queryString = queryParams.toString();
  const url = `/api/Foods${queryString ? `?${queryString}` : ''}`;
  return apiClient.get<PagedResult<FoodDto>>(url);
}

export async function getAllFoods(): Promise<FoodDto[]> {
  // Legacy function for backward compatibility - fetches all
  const result = await getFoods({ pageSize: 1000 });
  return result.items;
}

export async function getFoodById(id: string): Promise<FoodDto> {
  return apiClient.get<FoodDto>(`/api/Foods/${id}`);
}

export async function createFood(request: CreateFoodRequest): Promise<FoodDto> {
  return apiClient.post<CreateFoodRequest, FoodDto>('/api/Foods', request);
}

export async function updateFood(id: string, request: UpdateFoodRequest): Promise<void> {
  return apiClient.put<UpdateFoodRequest, void>(`/api/Foods/${id}`, request);
}

export async function deleteFood(id: string): Promise<void> {
  return apiClient.delete<void>(`/api/Foods/${id}`);
}

export async function addIngredientToFood(request: AddFoodIngredientRequest): Promise<void> {
  return apiClient.post<AddFoodIngredientRequest, void>(`/api/Foods/${request.foodId}/ingredients`, request);
}

export async function addImageToFood(request: AddFoodImageRequest): Promise<void> {
  return apiClient.post<AddFoodImageRequest, void>(`/api/Foods/${request.foodId}/images`, request);
}

export async function getAllCategories(): Promise<CategoryDto[]> {
  return apiClient.get<CategoryDto[]>('/api/Foods/categories');
}

export async function rateFood(foodId: string, rating: number): Promise<void> {
  return apiClient.post<{ rating: number }, void>(`/api/Foods/${foodId}/rating`, { rating });
}

